/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import {
  useState,
  useContext,
  useEffect,
  Dispatch,
  SetStateAction,
  useMemo,
  CSSProperties,
} from 'react';
import { useHistory } from 'react-router-dom';
import {
  Popover,
  Input,
  Select,
  Button,
  Tabs,
  SelectProps,
  Switch,
  Table,
  Tag as TagAntd,
} from 'antd';

import IconButton from '~components/_common/IconButton/IconButton';
import { useIntl } from 'react-intl';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';

import { AdvanceQueryType, SearchContext } from '~src/contexts/SearchContext';
import { useProjectId } from '~src/hooks/useProjectId';
import { SimpleTag } from '~components/popups-modals/EditFile.modal';
import { DB_STATE } from '~src/types/enums';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { getTagsForFile } from '~src/utils/tags';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface TagsGroupsColumnProps extends Partial<SelectProps<any>> {
  options: SelectProps<any>['options'];
  selectedGroups?: SimpleTag[];
  //setSelectedGroups: (groups: SimpleTag[]) => void;
}

enum FileType {
  ANY = 'type-any',
  FOLDERS = 'type-folders',
  FILES = 'type-files',
}

type OptionType = {
  value: string;
  label: string;
};

//initial state for advanced search
const initialAdvStateValue: AdvanceQueryType = {
  fileName: '',
  fileDesc: '',
  fileRevision: '',
  fileType: FileType.ANY,
  tags: [],
  allTagsPresent: true,
};
interface AdvancedSearchPopOverProps {
  isPopoverVisible: boolean;
  setIsPopoverVisible: Dispatch<SetStateAction<boolean>>;
}

export const AdvancedSearchPopOver: React.FC<AdvancedSearchPopOverProps> = ({
  isPopoverVisible,
  setIsPopoverVisible,
}) => {
  const { Option } = Select;

  const projectId = useProjectId();
  const intl = useIntl();
  const history = useHistory();
  const style = useStyles(styleFn);
  const advancedSearchMenuStyles = useStyles(advancedSearchMenuStylesFn);

  const archiveSettings = useArchiveSettings();
  const isTagsOn = archiveSettings?.data?.resArchiveSettings?.is_tags_on;

  const tagsData = archiveSettings.data?.resTags;
  const tagGroupsOptions = useMemo(() => {
    return (
      tagsData
        ?.filter((tag) => tag.state !== DB_STATE.REMOVED)
        .map((tag) => ({
          id: tag.id,
          value: String(tag.id),
          label: tag.title,
        })) || []
    );
  }, [tagsData]);

  const selectOptions: OptionType[] = [
    {
      value: FileType.ANY,
      label: intl.formatMessage({ id: FileType.ANY }),
    },
    {
      value: FileType.FOLDERS,
      label: intl.formatMessage({ id: FileType.FOLDERS }),
    },
    {
      value: FileType.FILES,
      label: intl.formatMessage({ id: FileType.FILES }),
    },
  ];

  //Default/Advanced search context
  const searchContext = useContext(SearchContext);

  //Advanced search state (4 inputs: Name, Description, Revision, Type)
  const [advancedQuery, setAdvancedQuery] = useState<AdvanceQueryType>(
    initialAdvStateValue
  );
  const [selectValue, setSelectValue] = useState(selectOptions[0].value);

  const handleAdvancedInputChange = (
    key: keyof AdvanceQueryType,
    value: boolean | string | string[]
  ) => {
    setAdvancedQuery((prevState) => ({ ...prevState, [key]: value }));
  };

  function updateFileType(option) {
    setSelectValue(option);

    setAdvancedQuery({
      ...advancedQuery,
      fileType: option,
      fileRevision: '',
    });
  }

  function onAdvancedSearchCancel() {
    setSelectValue(selectOptions[0].value);
    setAdvancedQuery(initialAdvStateValue);
    searchContext.advancedSearchHandler(initialAdvStateValue);
    setIsPopoverVisible(false);
  }

  function sendAdvancedQueryToContext() {
    searchContext.advancedSearchHandler(advancedQuery);
    history.push(`/projects/${projectId}/search`);
    setIsPopoverVisible(false);
  }

  function sendAdvancedQueryTagsToContext() {
    const { fileName, tags, allTagsPresent } = advancedQuery;
    const newAdvancedQuery = {
      ...initialAdvStateValue,
      allTagsPresent,
      tags,
      fileName,
    };

    searchContext.advancedSearchHandler(newAdvancedQuery);
    history.push(`/projects/${projectId}/search`);
    setIsPopoverVisible(false);
  }

  const isAdvancedQueryEmpty =
    advancedQuery?.fileName?.length === 0 &&
    advancedQuery?.fileDesc?.length === 0 &&
    advancedQuery?.fileRevision?.length === 0 &&
    advancedQuery?.fileType === FileType?.ANY &&
    advancedQuery?.allTagsPresent === true &&
    advancedQuery?.tags?.length === 0;

  // auto-open once on first mount if user is already on /search
  // and both default + advanced search are empty
  useEffect(() => {
    const isSearchInView = history.location.pathname.includes('/search');
    const isDefaultSearchEmpty =
      !searchContext.query || searchContext.query.trim() === '';

    if (isSearchInView && isDefaultSearchEmpty && isAdvancedQueryEmpty) {
      setIsPopoverVisible(true);
    }
  }, []);

  const TagsGroupsColumn: React.FC<TagsGroupsColumnProps> = ({
    options,
    selectedGroups = [],
    ...selectProps
  }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <div>
        {/*<FontAwesomeIcon*/}
        {/*  icon={['far', 'search']}*/}
        {/*  css={{ marginRight: '8px' }}*/}
        {/*  style={style.icons as CSSProperties}*/}
        {/*/>*/}
        <Select
          // className="borderless"
          showArrow={true}
          css={{ width: '100%' }}
          placeholder={intl.formatMessage({ id: 'choose-tags-placeholder' })}
          mode="tags"
          open={isOpen}
          onDropdownVisibleChange={(isItOpen) => {
            if (isOpen) {
              setIsOpen(isItOpen);
            }
          }}
          onClick={() => setIsOpen((prevState) => !prevState)}
          optionFilterProp="label"
          dropdownMatchSelectWidth={false}
          maxTagPlaceholder={() => ''}
          maxTagTextLength={5}
          options={options}
          value={advancedQuery.tags}
          tagRender={() => <div></div>}
          onSelect={(value: string) => {
            const newTags = [...(advancedQuery.tags || []), value];
            handleAdvancedInputChange('tags', newTags);
          }}
          onDeselect={(value: string) => {
            const newTags = (advancedQuery.tags || []).filter(
              (tag) => tag !== value
            );
            handleAdvancedInputChange('tags', newTags);
          }}
          {...selectProps}
        />
        {/*<div*/}
        {/*  onClick={() => setIsOpen((prevState) => !prevState)}*/}
        {/*  css={style.advancedSearchIcon}>*/}
        {/*  <IconButton*/}
        {/*    iconStyle="far"*/}
        {/*    icon="chevron-down"*/}
        {/*    customStyle={style.icons}*/}
        {/*  />*/}
        {/*</div>*/}
      </div>
    );
  };

  const tagsForFile = getTagsForFile(advancedQuery?.tags, tagsData);

  const singleCellData = tagsForFile?.map((tag) => (
    <TagAntd
      closable
      key={tag}
      onClose={() => {
        const id = tagsData?.find((item) => item?.title === tag)?.id;
        const newTags = advancedQuery?.tags?.filter(
          (tagItem) => tagItem !== String(id)
        );
        if (newTags) handleAdvancedInputChange('tags', newTags);
      }}
      css={{ marginBottom: '5px' }}
      color={'#1f5fa6'}>
      {tag}
    </TagAntd>
  ));

  const content = (
    <div css={advancedSearchMenuStyles.container}>
      <h3 css={advancedSearchMenuStyles.h3}>
        <IconButton
          iconStyle="far"
          icon="search"
          customStyle={style.icons}
          backgroundOff
        />
        {intl.formatMessage({ id: 'advanced-search' })}
      </h3>
      <Tabs defaultActiveKey="1">
        <Tabs.TabPane
          style={{ marginTop: 8 }}
          tab={intl.formatMessage({ id: 'advanced' })}
          key="1">
          {/* Advanced File Name Input */}
          <div css={advancedSearchMenuStyles.containerInfoIcon}>
            <IconButton
              icon={['far', 'info-circle']}
              customStyle={{ fontSize: '130%' }}
              containerStyle={{
                color: '#7a829b',
              }}
              backgroundOff
              tooltip={
                intl.locale === 'da' ? (
                  <div>Text Info</div>
                ) : (
                  <div>Text Info</div>
                )
              }
              widthTooltip="300px"
            />
          </div>
          <div css={advancedSearchMenuStyles.sections}>
            <div> {intl.formatMessage({ id: 'name' })} </div>
            <Input
              placeholder={intl.formatMessage({ id: 'search-name' })}
              allowClear
              onChange={(e) =>
                handleAdvancedInputChange('fileName', e.currentTarget.value)
              }
              onPressEnter={sendAdvancedQueryToContext}
              // value={clearAllAdvancedInputs ? '' : advancedQuery.fileName}
              value={advancedQuery.fileName}
              style={{ color: 'red' }}
              className={`${
                window.sessionStorage.getItem('appState') === 'superUser' &&
                'su-mode-input'
              }`}
            />
          </div>

          {/* Advanced File Description Input */}
          <div css={advancedSearchMenuStyles.sections}>
            <div> {intl.formatMessage({ id: 'description' })} </div>
            <Input
              placeholder={intl.formatMessage({ id: 'search-description' })}
              allowClear
              onChange={(e) =>
                handleAdvancedInputChange('fileDesc', e.currentTarget.value)
              }
              onPressEnter={sendAdvancedQueryToContext}
              value={advancedQuery.fileDesc}
              className={`${
                window.sessionStorage.getItem('appState') === 'superUser' &&
                'su-mode-input'
              }`}
            />
          </div>

          <div css={advancedSearchMenuStyles.sections}>
            <div> {intl.formatMessage({ id: 'revision-search' })} </div>

            <Input
              placeholder={intl.formatMessage({ id: 'search-revision' })}
              allowClear
              onChange={(e) =>
                handleAdvancedInputChange('fileRevision', e.currentTarget.value)
              }
              onPressEnter={sendAdvancedQueryToContext}
              value={advancedQuery.fileRevision}
              disabled={selectValue !== FileType.FILES}
              style={{
                opacity: selectValue !== FileType.FILES ? '.5' : '1',
              }}
              className={`${
                window.sessionStorage.getItem('appState') === 'superUser' &&
                'su-mode-input'
              }`}
            />
          </div>

          <div css={advancedSearchMenuStyles.sections}>
            <div> {intl.formatMessage({ id: 'type' })} </div>
            <Select
              onChange={updateFileType}
              defaultValue={selectOptions[0].value}
              css={advancedSearchMenuStyles.selectMenu}
              value={selectValue}
              className={`${
                window.sessionStorage.getItem('appState') === 'superUser' &&
                'su-select'
              }`}>
              {selectOptions.map((el) => (
                <Option
                  key={`${el.value}_${el.label}`}
                  value={el.value}
                  className={`${
                    window.sessionStorage.getItem('appState') === 'superUser' &&
                    'su-select'
                  }`}>
                  {el.label}
                </Option>
              ))}
            </Select>
          </div>
          <div css={advancedSearchMenuStyles.buttons}>
            <Button
              css={advancedSearchMenuStyles.button}
              className={`${
                window.sessionStorage.getItem('appState') === 'superUser' &&
                'su-button'
              }`}
              onClick={() => onAdvancedSearchCancel()}>
              {intl.formatMessage({ id: 'cancel' })}
            </Button>
            <Button
              css={advancedSearchMenuStyles.button}
              type="primary"
              disabled={
                advancedQuery.fileName.length === 0 &&
                advancedQuery.fileDesc.length === 0 &&
                advancedQuery.fileRevision.length === 0
              }
              onClick={() => sendAdvancedQueryToContext()}>
              {intl.formatMessage({ id: 'search' })}
            </Button>
          </div>
        </Tabs.TabPane>

        {isTagsOn && (
          <Tabs.TabPane
            style={{ minWidth: '350px', maxWidth: '440px', marginTop: 8 }}
            tab={intl.formatMessage({ id: 'tags' })}
            key="2">
            <div css={advancedSearchMenuStyles.containerInfoIcon}>
              <IconButton
                icon={['far', 'info-circle']}
                customStyle={{ fontSize: '130%' }}
                containerStyle={{
                  color: '#7a829b',
                }}
                backgroundOff
                tooltip={
                  intl.locale === 'da' ? (
                    <div>Text Info Tags</div>
                  ) : (
                    <div>Text Info Tags</div>
                  )
                }
                widthTooltip="300px"
              />
            </div>
            <div css={advancedSearchMenuStyles.sections}>
              <div> {intl.formatMessage({ id: 'name' })} </div>
              <Input
                placeholder={intl.formatMessage({ id: 'search-name' })}
                allowClear
                onChange={(e) =>
                  handleAdvancedInputChange('fileName', e.currentTarget.value)
                }
                onPressEnter={sendAdvancedQueryTagsToContext}
                // value={clearAllAdvancedInputs ? '' : advancedQuery.fileName}
                value={advancedQuery.fileName}
                style={{ color: 'red' }}
                className={`${
                  window.sessionStorage.getItem('appState') === 'superUser' &&
                  'su-mode-input'
                }`}
              />
            </div>
            <div style={{ display: 'flex' }}>
              <div css={advancedSearchMenuStyles.sections}>
                <div> {intl.formatMessage({ id: 'add-tags' })} </div>
                <TagsGroupsColumn options={tagGroupsOptions} />
              </div>
              <div css={{ marginLeft: '50px', marginTop: '5px' }}>
                <div>{intl.formatMessage({ id: 'all-tags-present' })}</div>
                <Switch
                  checked={advancedQuery.allTagsPresent}
                  onChange={(checked) =>
                    handleAdvancedInputChange('allTagsPresent', checked)
                  }
                />
              </div>
            </div>
            <div css={{ marginTop: '15px', width: '100%' }}>
              <Table
                showSorterTooltip={false}
                bordered
                pagination={false}
                columns={[
                  {
                    title: intl.formatMessage({ id: 'tags' }),
                    dataIndex: 'tags',
                    key: 'tags',
                  },
                ]}
                dataSource={
                  singleCellData
                    ? [
                        {
                          key: 'tags',
                          tags: singleCellData,
                        },
                      ]
                    : []
                }
              />
            </div>
            <div css={advancedSearchMenuStyles.buttons}>
              <Button
                css={advancedSearchMenuStyles.button}
                className={`${
                  window.sessionStorage.getItem('appState') === 'superUser' &&
                  'su-button'
                }`}
                onClick={() => onAdvancedSearchCancel()}>
                {intl.formatMessage({ id: 'cancel' })}
              </Button>
              <Button
                css={advancedSearchMenuStyles.button}
                type="primary"
                disabled={advancedQuery?.tags?.length === 0}
                onClick={() => sendAdvancedQueryTagsToContext()}>
                {intl.formatMessage({ id: 'search' })}
              </Button>
            </div>
          </Tabs.TabPane>
        )}
      </Tabs>
    </div>
  );

  return (
    <Popover
      content={<div>{content}</div>}
      visible={isPopoverVisible}
      onVisibleChange={() => setIsPopoverVisible((prevState) => !prevState)}
      placement="bottomRight"
      trigger="click">
      <div css={style.advancedSearchIcon}>
        <IconButton
          iconStyle="far"
          icon="chevron-down"
          customStyle={style.icons}
          tooltip={intl.formatMessage({ id: 'advanced-search' })}
        />
      </div>
    </Popover>
  );
};
const styleFn: ThemedStyleFn = (theme) => ({
  component: {
    width: theme.aboveWidth[1],
    minWidth: 154,
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${theme.colors.lightGray}`,
    borderRadius: '2px',
    margin: '0 5px',
    padding: '0 5px',
    // width: '70%',
    height: '32px',
    ':focus': {
      border: `1px solid ${theme.colors.primary}`,
    },
    backgroundColor: theme.colors.gray,
  },
  icons: {
    color: theme.colors.text.primary,
    fontSize: '16px',
  },
  advancedSearchIcon: {
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  inputField: {
    border: 'none!important',
    margin: '0 5px',
    height: '100%',
    width: '80%',
    color: `${theme.colors.text.primary}`,

    '::placeholder': {
      color: `${theme.colors.text.secondary}`,
    },

    ':focus': {
      outline: 'none',
    },
  },
});

const advancedSearchMenuStylesFn: ThemedStyleFn = (theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: '2px 15px',
  },
  h3: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.text.primary,
  },
  sections: {
    marginTop: '5px',
    width: '270px',
  },
  selectMenu: {
    width: '90px',
    backgroundColor: theme.colors.gray,
  },
  checkbox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkboxDiv: {
    display: 'flex',
    flexDirection: 'column',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '10px',
  },
  button: {
    margin: '7px',
    backgroundColor: theme.colors.blue,
  },
  input: {
    backgroundColor: theme.colors.lightGray,
  },
  currentFolderText: {
    color: theme.colors.text.secondary,
    fontSize: '13px',
  },
  containerInfoIcon: { display: 'flex', justifyContent: 'flex-end' },
});
